/**
 * Onboarding Context
 *
 * Global onboarding state and MUI tour engine orchestration with backend progress.
 */

import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  useRef,
  ReactNode,
} from "react";
import { useAuth } from "./auth/authContext";
import onboardingService from "../services/onboardingService";
import { logger } from "../utils/logger";
import { hasRole } from "../utils/roleHelpers";
import { isViteFlagEnabled } from '../utils/runtimePlatformFlags';
import {
  filterValidSteps,
  filterToursByRole,
  elementExists,
  validateTourFlow,
} from "../utils/tourHelpers";
import { TOUR_DOM_EVENTS, cleanupNavTourDropdowns } from "../components/onboarding/onboardingDomEvents";
import { waitForTarget } from "../components/onboarding/engine/waitForTarget";
import { enrichClientTourFlows } from "../config/onboardingTours";
import {
  computePlatformCompletionPercentage,
  filterPlatformTours,
  getLabMissionTourSlugs,
  isLabMissionTour,
} from "../../shared/onboarding/tourCatalog";
import { ONBOARDING_EVENTS } from "../components/onboarding/onboardingEventNames";
import { trackEvent } from "../utils/analytics";
import {
  EVENTS_CREATE_FIRST_PROGRESS_ALIAS,
  LEGACY_EVENTS_CREATE_FIRST_SLUG,
} from "../../shared/onboarding/coordinatorLearningLabTours";
import {
  clearAllMissionSessionsForUser,
  clearMissionSession,
  loadMissionSession,
  resolveMissionTourStepIndex,
  saveMissionSession,
  setCreateMissionDraftReady,
} from "../../shared/onboarding/missionSessionStorage";
import {
  dashboardReadyTargetForRole,
  dashboardRoleForTourFlow,
} from "../../shared/onboarding/dashboardTourRoles";
import type {
  TourStep,
  OnboardingFlow,
  OnboardingProgress,
  OnboardingConfig,
  TourManagerState,
  UserOnboardingStep,
} from "../types/onboarding";

/**
 * Onboarding context type
 */
interface OnboardingContextType {
  /** Current tour manager state */
  state: TourManagerState;
  /** Start a tour by flow ID */
  startTour: (flowId: string) => Promise<void>;
  /** Stop the current tour */
  stopTour: (completedFlowId?: string) => void;
  /** Skip the current tour */
  skipTour: () => void;
  /** Reset onboarding progress */
  resetOnboarding: () => Promise<void>;
  /** Get available tours for current user role */
  getAvailableTours: () => OnboardingFlow[];
  /** Get all active tours (without role filtering) - for display purposes */
  getAllTours: () => OnboardingFlow[];
  /** Check if a flow is completed */
  isFlowCompleted: (flowId: string) => boolean;
  /** Check if a step is completed */
  isStepCompleted: (stepName: string) => boolean;
  /** Complete a step */
  completeStep: (
    stepName: string,
    metadata?: Record<string, any>,
  ) => Promise<void>;
  /** Update step progress */
  updateStepProgress: (
    stepName: string,
    timeSpent: number,
    attempts: number,
  ) => Promise<void>;
  /** Update the current step index */
  setCurrentStepIndex: (index: number) => void;
  /** Get onboarding configuration */
  config: OnboardingConfig;
  /** Update configuration */
  updateConfig: (updates: Partial<OnboardingConfig>) => void;
}

/**
 * Initial tour manager state
 */
const initialState: TourManagerState = {
  isRunning: false,
  currentFlowId: undefined,
  currentStepIndex: 0,
  currentSteps: [],
  currentRunTotalSteps: 0,
  currentRunStepNames: [],
  progress: null,
  templates: new Map(),
  isLoading: false,
  error: null,
};

/**
 * Default onboarding configuration
 */
const defaultConfig: OnboardingConfig = {
  enabled: true,
  autoStart: true,
  trackProgress: true,
  allowSkip: true,
  showSkipButton: true,
  showProgress: true,
  spotlightPadding: 8,
  zIndex: 2000,
  disableScroll: true,
  disableCloseOnEsc: false,
  showSkipConfirmation: true,
};

function isDashboardTourPath(pathname: string): boolean {
  return pathname === "/dashboard" || pathname.endsWith("/dashboard");
}

async function ensureDashboardRoleForTour(flowId: string): Promise<void> {
  const roleKey = dashboardRoleForTourFlow(flowId);
  if (!roleKey || !isDashboardTourPath(window.location.pathname)) {
    return;
  }

  window.dispatchEvent(
    new CustomEvent(TOUR_DOM_EVENTS.SWITCH_DASHBOARD_ROLE, {
      detail: { roleKey },
    }),
  );

  const readyTarget = dashboardReadyTargetForRole(roleKey);
  if (readyTarget) {
    const ready = await waitForTarget(readyTarget, { timeoutMs: 8000 });
    if (!ready) {
      await new Promise((resolve) => window.setTimeout(resolve, 500));
    }
    return;
  }

  await new Promise((resolve) => window.setTimeout(resolve, 400));
}

// Create the context
const OnboardingContext = createContext<OnboardingContextType | undefined>(
  undefined,
);

/**
 * Onboarding Provider Props
 */
interface OnboardingProviderProps {
  children: ReactNode;
  /** Custom tours configuration */
  tours?: OnboardingFlow[];
  /** Custom configuration */
  config?: Partial<OnboardingConfig>;
}

/**
 * Onboarding Provider Component
 */
export const OnboardingProvider: React.FC<OnboardingProviderProps> = ({
  children,
  tours: manifestTours = [],
  config: customConfig = {},
}) => {
  const { user, isAuthenticated } = useAuth();
  const [resolvedTours, setResolvedTours] = useState<OnboardingFlow[]>(manifestTours);
  const [state, setState] = useState<TourManagerState>(initialState);
  const [config, setConfig] = useState<OnboardingConfig>({
    ...defaultConfig,
    ...customConfig,
  });
  const [systemEnabled, setSystemEnabled] = useState<boolean | null>(null); // null = loading
  const stepStartTimeRef = useRef<number>(0);
  const stepAttemptsRef = useRef<Map<string, number>>(new Map());
  const previewTourHandledRef = useRef(false);
  const lastUserIdRef = useRef<number | null>(null);

  useEffect(() => {
    setResolvedTours(enrichClientTourFlows(manifestTours));
  }, [manifestTours]);

  /**
   * Load merged tour catalog from API (manifest fallback via prop).
   */
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    let cancelled = false;

    const loadCatalog = async () => {
      const catalogTours = await onboardingService.getCatalog();
      if (cancelled || catalogTours.length === 0) {
        return;
      }
      setResolvedTours(enrichClientTourFlows(catalogTours));
    };

    void loadCatalog();

    return () => {
      cancelled = true;
    };
  }, [isAuthenticated]);

  /**
   * Load system-wide onboarding setting from backend
   */
  useEffect(() => {
    let isMounted = true;

    const loadSystemSetting = async () => {
      try {
        const response = await fetch("/api/settings/onboarding", {
          credentials: "include",
        });

        if (!isMounted) return;

        if (response.ok) {
          const data = await response.json();
          if (data.success) {
            if (isMounted) {
              setSystemEnabled(data.data.enabled);
              // Update config with system-wide setting
              setConfig((prev) => ({
                ...prev,
                enabled: data.data.enabled,
              }));
            }
          } else {
            // Default to enabled if fetch fails
            if (isMounted) {
              setSystemEnabled(true);
            }
          }
        } else {
          // Default to enabled if fetch fails
          if (isMounted) {
            setSystemEnabled(true);
          }
        }
      } catch (error) {
        if (isMounted) {
          logger.warn(
            "Failed to load system-wide onboarding setting, defaulting to enabled",
            { error },
          );
          // Default to enabled if fetch fails
          setSystemEnabled(true);
        }
      }
    };

    loadSystemSetting();

    return () => {
      isMounted = false;
    };
  }, []);

  /**
   * Get available tours for current user role
   */
  const getAvailableTours = useCallback((): OnboardingFlow[] => {
    if (!user) {
      return [];
    }

    // Filter tours by user roles (handles multiple roles)
    return filterToursByRole(resolvedTours, user);
  }, [resolvedTours, user]);

  /**
   * Get all active tours (without role filtering) - for display purposes
   */
  const getAllTours = useCallback((): OnboardingFlow[] => {
    // Return all active tours, filtered only by active status
    return resolvedTours.filter((tour) => tour.active);
  }, [resolvedTours]);

  /**
   * Load user onboarding progress
   */
  const loadProgress = useCallback(async () => {
    if (!isAuthenticated || !user?.id) {
      return;
    }

    try {
      setState((prev) => ({ ...prev, isLoading: true, error: null }));

      const progress = await onboardingService.getOnboardingStatus();
      const steps = await onboardingService.getUserOnboardingProgress();

      if (progress) {
        const platformTours = filterPlatformTours(getAvailableTours());
        const completionPercentage = computePlatformCompletionPercentage(
          progress.completedSteps || [],
          platformTours,
        );

        setState((prev) => ({
          ...prev,
          progress: {
            ...progress,
            completedFlows: Array.from(
              new Set([
                ...(progress.completedFlows || []),
                ...(prev.progress?.completedFlows || []),
              ]),
            ),
            startedFlows: Array.from(
              new Set([
                ...(progress.startedFlows || []),
                ...(prev.progress?.startedFlows || []),
              ]),
            ),
            completionPercentage,
          },
          isLoading: false,
        }));
      } else {
        setState((prev) => ({
          ...prev,
          isLoading: false,
        }));
      }

      // Cache steps for quick lookup
      if (steps && steps.length > 0) {
        const completedStepsMap = new Map<string, UserOnboardingStep>();
        steps.forEach((step) => {
          completedStepsMap.set(step.onboarding_step, step);
        });
        // Store in state for quick access
      }
    } catch (error) {
      logger.error("Error loading onboarding progress", { error });
      setState((prev) => ({
        ...prev,
        isLoading: false,
        error:
          error instanceof Error ? error.message : "Failed to load progress",
      }));
    }
  }, [isAuthenticated, user?.id, getAvailableTours]);

  /**
   * Initialize onboarding on mount
   */
  useEffect(() => {
    if (!isAuthenticated || !user?.id) {
      return;
    }

    void loadProgress();
  }, [isAuthenticated, user?.id, loadProgress]);

  /**
   * Check if a flow is completed
   */
  const isFlowCompleted = useCallback(
    (flowId: string): boolean => {
      const completed = state.progress?.completedFlows || [];
      if (completed.includes(flowId)) {
        return true;
      }
      if (
        flowId === LEGACY_EVENTS_CREATE_FIRST_SLUG ||
        flowId === EVENTS_CREATE_FIRST_PROGRESS_ALIAS
      ) {
        return (
          completed.includes(LEGACY_EVENTS_CREATE_FIRST_SLUG) ||
          completed.includes(EVENTS_CREATE_FIRST_PROGRESS_ALIAS)
        );
      }
      return false;
    },
    [state.progress],
  );

  /**
   * Check if a step is completed
   */
  const isStepCompleted = useCallback(
    (stepName: string): boolean => {
      return (state.progress?.completedSteps || []).includes(stepName);
    },
    [state.progress],
  );

  /**
   * Start a tour
   */
  const startTour = useCallback(
    async (flowId: string) => {
      // Check system-wide setting first - if disabled, tours are disabled regardless of local config
      if (systemEnabled === false) {
        logger.warn("Onboarding is disabled system-wide", { flowId });
        return;
      }

      // If system setting is still loading, use local config
      // If system setting is enabled, still check local config
      const isEnabled = systemEnabled !== null ? systemEnabled : config.enabled;
      if (!isEnabled || !config.enabled) {
        logger.warn("Onboarding is disabled", {
          flowId,
          systemEnabled,
          configEnabled: config.enabled,
        });
        return;
      }

      const flow = resolvedTours.find((t) => t.id === flowId);
      if (!flow) {
        logger.error("Tour flow not found", { flowId });
        return;
      }

      if (
        state.isRunning &&
        state.currentFlowId === flowId &&
        isLabMissionTour(flowId)
      ) {
        logger.debug("Lab mission tour already running", { flowId });
        return;
      }

      const validationErrors = validateTourFlow(flow);
      if (validationErrors.length > 0) {
        logger.warn("Tour flow failed validation and will not start", {
          flowId,
          errors: validationErrors,
        });
        return;
      }

      // Check prerequisites
      for (const prerequisite of flow.prerequisites) {
        if (!isFlowCompleted(prerequisite)) {
          logger.warn("Prerequisites not met", { flowId, prerequisite });
          return;
        }
      }

      await ensureDashboardRoleForTour(flowId);

      if (isLabMissionTour(flowId) && user?.id) {
        for (const otherSlug of getLabMissionTourSlugs()) {
          if (otherSlug !== flowId) {
            clearMissionSession(user.id, otherSlug);
          }
        }
      }

      if (flowId === 'nav_reference') {
        cleanupNavTourDropdowns(flowId);
        await waitForTarget('[data-tour-stable="nav-logo"]', { timeoutMs: 5000 });
      }

      // Filter steps based on element existence and skip conditions
      // For dropdown items, skip element existence check (they're in portals and only exist when menu is open)
      const stepsWithValidTargets = flow.steps.filter((step) => {
        if (!step.target) {
          return true; // No target means center placement - always valid
        }

        // Check if this is a dropdown item
        const target = step.target;
        const isBrowseDropdownItem =
          target.includes("nav-browse-") &&
          !target.includes("nav-browse-button");
        const isAboutDropdownItem =
          target.includes("nav-about-us-") &&
          !target.includes("nav-about-us-button");
        const isUserDropdownItem =
          target.includes("nav-user-") &&
          target !== '[data-tour="nav-user-menu"]' &&
          target !== '[data-tour="nav-theme-toggle"]';

        const isAttendeePanelTarget =
          target.includes("attendee-tab-se-applications") ||
          target.includes("attendee-tab-se-assignments") ||
          target.includes("attendee-training-geo-alerts");

        const isModulePanelTarget =
          target.includes("payout-status") ||
          target.includes("payout-info") ||
          target.includes("stripe-setup-button") ||
          step.metadata?.isPanelTarget === true;

        const isDropdownItem =
          isBrowseDropdownItem || isAboutDropdownItem || isUserDropdownItem;

        // Skip element existence check for dropdown items - they'll be validated when dropdown opens
        if (isDropdownItem || isAttendeePanelTarget || isModulePanelTarget) {
          return true;
        }

        // For non-dropdown items, check element existence
        return elementExists(step.target);
      });

      // Filter steps based on async skip conditions
      const validSteps = await filterValidSteps(stepsWithValidTargets);

      if (validSteps.length === 0) {
        logger.warn("No valid steps for tour", { flowId });
        return;
      }

      // Ensure the first step is actually valid and has a target that exists
      // This prevents the tour from starting with an invalid first step
      if (validSteps[0]?.target) {
        const firstStepTarget = validSteps[0].target;
        const firstStepExists = elementExists(firstStepTarget);
        if (!firstStepExists) {
          logger.error("First step target does not exist - cannot start tour", {
            flowId,
            target: firstStepTarget,
            stepName: validSteps[0].stepName,
          });
          return;
        }
      }

      // Collapse help speed dial (and tour picker) whenever a tour run begins.
      window.dispatchEvent(new CustomEvent(TOUR_DOM_EVENTS.CLOSE_HELP_SPEED_DIAL));

      const missionMode = isLabMissionTour(flowId);
      let restoredIndex = 0;
      let restoredCompleted: string[] = [];
      if (missionMode && user?.id) {
        const snapshot = loadMissionSession(user.id, flowId);
        if (snapshot) {
          restoredIndex = resolveMissionTourStepIndex(validSteps, snapshot);
          restoredCompleted = snapshot.completedIds;
        }
      }

      setState((prev) => ({
        ...prev,
        isRunning: true,
        currentFlowId: flowId,
        currentStepIndex: restoredIndex,
        currentSteps: validSteps,
        currentRunTotalSteps: validSteps.length,
        currentRunStepNames: validSteps
          .map((step) => step.stepName)
          .filter((stepName): stepName is string => Boolean(stepName)),
        missionMode,
        missionSlug: missionMode ? flowId : undefined,
        missionCompletedStepIds: missionMode ? restoredCompleted : undefined,
        error: null,
      }));

      if (missionMode && user?.id) {
        const startStep = validSteps[restoredIndex];
        const stepId =
          typeof startStep?.metadata?.missionStepId === 'string'
            ? startStep.metadata.missionStepId
            : undefined;
        stepStartTimeRef.current = Date.now();
        saveMissionSession(user.id, flowId, {
          stepIndex: restoredIndex,
          stepId,
          completedIds: restoredCompleted,
          startedAt: stepStartTimeRef.current,
        });
      }

      // Mark flow as started
      if (
        state.progress &&
        !(state.progress.startedFlows || []).includes(flowId)
      ) {
        const updatedProgress: OnboardingProgress = {
          ...state.progress,
          startedFlows: [...(state.progress.startedFlows || []), flowId],
        };
        setState((prev) => ({ ...prev, progress: updatedProgress }));
      }

      const shouldTrack = config.trackProgress === true;
      // Track first step start (non-blocking - don't let API errors prevent tour from starting)
      if (shouldTrack && validSteps[0]?.stepName) {
        stepStartTimeRef.current = Date.now();
        const startIndex = restoredIndex;
        const startStep = validSteps[startIndex];
        if (startStep?.stepName) {
          const attempts =
            stepAttemptsRef.current.get(startStep.stepName) || 0;
          stepAttemptsRef.current.set(startStep.stepName, attempts + 1);
          onboardingService
            .markStepStarted(startStep.stepName, startIndex + 1, {
              flowId,
              stepIndex: startIndex,
            })
            .catch((error) => {
              logger.warn("Failed to mark step as started (non-blocking)", {
                error,
                stepName: startStep.stepName,
                flowId,
              });
            });
        }
      }
    },
    [
      config.enabled,
      config.trackProgress,
      resolvedTours,
      state.currentFlowId,
      state.isRunning,
      state.progress,
      isFlowCompleted,
      user?.id,
    ],
  );

  /**
   * CMS preview: ?tourPreview=<slug> (admin + ONBOARDING_CMS_ENABLED only).
   */
  useEffect(() => {
    if (!isAuthenticated || previewTourHandledRef.current || state.isRunning) {
      return;
    }

    const params = new URLSearchParams(window.location.search);
    const previewSlug = params.get("tourPreview")?.trim();
    if (!previewSlug) {
      return;
    }

    const cmsPreviewEnabled =
      isViteFlagEnabled('VITE_ONBOARDING_CMS_ENABLED', import.meta.env.VITE_ONBOARDING_CMS_ENABLED) && hasRole(user, "admin");
    if (!cmsPreviewEnabled) {
      logger.warn("Ignoring tourPreview — admin CMS preview not enabled for this session", {
        previewSlug,
      });
      return;
    }

    const previewTour = resolvedTours.find((t) => t.id === previewSlug);
    if (!previewTour) {
      return;
    }

    const previewRole = params.get("previewRole")?.trim();
    if (previewRole) {
      sessionStorage.setItem("apt_onboarding_preview_role", previewRole);
    }

    previewTourHandledRef.current = true;
    void startTour(previewSlug);
  }, [isAuthenticated, resolvedTours, startTour, state.isRunning, user]);

  /**
   * Stop the current tour
   */
  const stopTour = useCallback(
    (completedFlowId?: string) => {
      let shouldCleanupNavDropdowns = false;
      let missionFlowId: string | undefined;
      let wasMissionMode = false;

      setState((prev) => {
        shouldCleanupNavDropdowns =
          prev.currentFlowId === 'nav_reference' ||
          completedFlowId === 'nav_reference';
        wasMissionMode = prev.missionMode === true;
        missionFlowId = prev.currentFlowId;

        const baseProgress: OnboardingProgress | null =
          prev.progress ??
          (user
            ? {
                userId: user.id,
                completedSteps: [],
                startedFlows: [],
                completedFlows: [],
                skippedFlows: [],
                completionPercentage: 0,
                lastUpdated: new Date(),
              }
            : null);
        const shouldAddFlow =
          completedFlowId &&
          baseProgress &&
          !(baseProgress.completedFlows || []).includes(completedFlowId);
        const updatedProgress =
          shouldAddFlow && baseProgress
            ? {
                ...baseProgress,
                completedFlows: [
                  ...(baseProgress.completedFlows || []),
                  completedFlowId,
                ],
                lastUpdated: new Date(),
              }
            : baseProgress;

        return {
          ...prev,
          progress: updatedProgress ?? prev.progress,
          isRunning: false,
          currentFlowId: undefined,
          currentStepIndex: 0,
          currentSteps: [],
          currentRunTotalSteps: 0,
          currentRunStepNames: [],
          missionMode: false,
          missionSlug: undefined,
          missionCompletedStepIds: undefined,
        };
      });
      stepStartTimeRef.current = 0;
      if (!wasMissionMode || completedFlowId) {
        setCreateMissionDraftReady(false);
      }

      if (completedFlowId && wasMissionMode && user?.id && missionFlowId) {
        clearMissionSession(user.id, missionFlowId);
      }

      // Keep mission session snapshots on incomplete stops so SPA navigation can resume.

      if (shouldCleanupNavDropdowns) {
        cleanupNavTourDropdowns('nav_reference');
      }

      void (async () => {
        if (completedFlowId && config.trackProgress) {
          try {
            await onboardingService.completeFlow(completedFlowId);
          } catch (error) {
            logger.warn("Failed to persist completed flow", {
              error,
              completedFlowId,
            });
          }
        }
        await loadProgress();
      })();
    },
    [config.trackProgress, loadProgress, user],
  );

  /**
   * Skip the current tour
   */
  const skipTour = useCallback(() => {
    const skippedFlowId = state.currentFlowId;
    const wasMission = state.missionMode === true;

    if (skippedFlowId) {
      if (wasMission && user?.id) {
        clearMissionSession(user.id, skippedFlowId);
        setCreateMissionDraftReady(false);
        trackEvent({
          action: ONBOARDING_EVENTS.LEARNING_LAB_MISSION_ABANDONED,
          category: 'onboarding',
          label: skippedFlowId,
          custom_parameters: { learning_lab_active: true },
        });
      }

      if (config.trackProgress) {
        onboardingService.skipFlow(skippedFlowId).catch((error) => {
          logger.warn("Failed to persist skipped flow", {
            error,
            flowId: skippedFlowId,
          });
        });
      }

      const updatedProgress: OnboardingProgress = {
        ...(state.progress ?? {
          userId: user?.id ?? 0,
          completedSteps: [],
          startedFlows: [],
          completedFlows: [],
          skippedFlows: [],
          completionPercentage: 0,
          lastUpdated: new Date(),
        }),
        skippedFlows: Array.from(
          new Set([
            ...(state.progress?.skippedFlows || []),
            skippedFlowId,
          ]),
        ),
      };
      setState((prev) => ({
        ...prev,
        progress: updatedProgress,
        isRunning: false,
        currentFlowId: undefined,
        currentStepIndex: 0,
        currentSteps: [],
        currentRunTotalSteps: 0,
        currentRunStepNames: [],
        missionMode: false,
        missionSlug: undefined,
        missionCompletedStepIds: undefined,
      }));
    }
    stepStartTimeRef.current = 0;

    if (skippedFlowId === 'nav_reference') {
      cleanupNavTourDropdowns('nav_reference');
    }
  }, [config.trackProgress, state.currentFlowId, state.missionMode, state.progress, user?.id]);

  useEffect(() => {
    if (user?.id) {
      lastUserIdRef.current = Number(user.id);
    }
  }, [user?.id]);

  useEffect(() => {
    if (!isAuthenticated && lastUserIdRef.current != null) {
      clearAllMissionSessionsForUser(lastUserIdRef.current);
      setCreateMissionDraftReady(false);
      lastUserIdRef.current = null;
    }
  }, [isAuthenticated]);

  useEffect(() => {
    const onStopActiveTour = () => {
      if (state.isRunning) {
        stopTour();
      }
    };
    window.addEventListener(TOUR_DOM_EVENTS.STOP_ACTIVE_TOUR, onStopActiveTour);
    return () =>
      window.removeEventListener(
        TOUR_DOM_EVENTS.STOP_ACTIVE_TOUR,
        onStopActiveTour,
      );
  }, [state.isRunning, stopTour]);

  /**
   * Complete a step
   */
  const completeStep = useCallback(
    async (stepName: string, metadata?: Record<string, any>) => {
      const timeSpent = stepStartTimeRef.current
        ? Math.floor((Date.now() - stepStartTimeRef.current) / 1000)
        : 0;
      const shouldTrack = config.trackProgress === true;
      if (shouldTrack) {
        const success = await onboardingService.completeStep(stepName, {
          ...metadata,
          flowId: state.currentFlowId,
          timeSpent,
        });
        if (!success) {
          logger.warn("Step completion was not persisted to backend", {
            stepName,
            flowId: state.currentFlowId,
          });
        }
      }

      const baseProgress: OnboardingProgress = state.progress ?? {
        userId: user?.id ?? 0,
        completedSteps: [],
        startedFlows: [],
        completedFlows: [],
        skippedFlows: [],
        completionPercentage: 0,
        lastUpdated: new Date(),
      };
      // Update local progress
      const updatedProgress: OnboardingProgress = {
        ...baseProgress,
        completedSteps: Array.from(
          new Set([...(baseProgress.completedSteps || []), stepName]),
        ),
        startedFlows: baseProgress.startedFlows || [],
        completedFlows: baseProgress.completedFlows || [],
        skippedFlows: baseProgress.skippedFlows || [],
        lastUpdated: new Date(),
      };

      const platformTours = filterPlatformTours(getAvailableTours());
      updatedProgress.completionPercentage = computePlatformCompletionPercentage(
        updatedProgress.completedSteps || [],
        platformTours,
      );

      setState((prev) => ({
        ...prev,
        progress: updatedProgress,
      }));

      stepStartTimeRef.current = 0;
    },
    [
      config.trackProgress,
      state.currentFlowId,
      state.progress,
      getAvailableTours,
      user?.id,
    ],
  );

  /**
   * Update step progress
   */
  const updateStepProgress = useCallback(
    async (stepName: string, timeSpent: number, attempts: number) => {
      if (!config.trackProgress) {
        return;
      }
      await onboardingService.updateStepProgress(stepName, timeSpent, attempts);
    },
    [config.trackProgress],
  );

  /**
   * Update the current step index (for UI sync)
   */
  const setCurrentStepIndex = useCallback((index: number) => {
    setState((prev) => ({
      ...prev,
      currentStepIndex: Math.max(
        0,
        Math.min(index, prev.currentSteps.length - 1),
      ),
    }));
  }, []);

  /**
   * Reset onboarding
   */
  const resetOnboarding = useCallback(async () => {
    const success = await onboardingService.resetOnboarding();
    if (success) {
      setState(initialState);
      await loadProgress();
      return;
    }
    throw new Error("Failed to reset onboarding progress");
  }, [loadProgress]);

  /**
   * Update configuration
   */
  const updateConfig = useCallback((updates: Partial<OnboardingConfig>) => {
    setConfig((prev) => ({ ...prev, ...updates }));
  }, []);

  const value: OnboardingContextType = {
    state,
    startTour,
    stopTour,
    skipTour,
    resetOnboarding,
    getAvailableTours,
    getAllTours,
    isFlowCompleted,
    isStepCompleted,
    completeStep,
    updateStepProgress,
    setCurrentStepIndex,
    config,
    updateConfig,
  };

  return (
    <OnboardingContext.Provider value={value}>
      {children}
    </OnboardingContext.Provider>
  );
};

/**
 * Hook to access onboarding context
 */
export const useOnboarding = (): OnboardingContextType => {
  const context = useContext(OnboardingContext);
  if (context === undefined) {
    throw new Error("useOnboarding must be used within an OnboardingProvider");
  }
  return context;
};

/**
 * Hook to access current tour step (if tour is running)
 */
export const useTourStep = (): {
  step: TourStep | null;
  index: number;
  total: number;
} => {
  const { state } = useOnboarding();
  const currentSteps = state.currentSteps || [];
  const currentStep =
    state.isRunning && currentSteps.length > 0
      ? currentSteps[state.currentStepIndex] || null
      : null;

  return {
    step: currentStep,
    index: state.currentStepIndex,
    total: currentSteps.length,
  };
};

export default OnboardingContext;
